import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ESCAPE, UP_ARROW } from '@angular/cdk/keycodes';
import { Overlay, OverlayConfig, OverlayRef, PositionStrategy, ScrollStrategy } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import {
   ChangeDetectionStrategy,
   Component,
   ComponentRef,
   EventEmitter,
   Inject,
   InjectionToken,
   Input,
   NgZone,
   OnDestroy,
   Output,
   ViewContainerRef,
} from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { MatCalendarCellCssClasses } from '@angular/material/datepicker';
import { merge, Subject, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { LcsDateTimeAdapter } from '../configuration/date-time-adapter';
import { LcsDateTimePickerContentComponent } from '../date-time-picker-content/date-time-picker-content.component';
import { createDateTimePickerError } from '../date-time-picker-errors';
import { LcsDateTimePickerInputDirective } from '../date-time-picker-input/date-time-picker-input.directive';

/** Used to generate a unique ID for each datepicker instance. */
let dateTimePickerUid = 0;

/** Injection token that determines the scroll handling while the calendar is open. */
export const LCS_DATE_TIME_PICKER_SCROLL_STRATEGY = new InjectionToken<() => ScrollStrategy>(
   'lcs-date-time-picker-scroll-strategy'
);

export function LCS_DATE_TIME_PICKER_SCROLL_STRATEGY_FACTORY(overlay: Overlay): () => ScrollStrategy {
   return () => overlay.scrollStrategies.reposition();
}

export const LCS_DATE_TIME_PICKER_SCROLL_STRATEGY_FACTORY_PROVIDER = {
   provide: LCS_DATE_TIME_PICKER_SCROLL_STRATEGY,
   deps: [Overlay],
   useFactory: LCS_DATE_TIME_PICKER_SCROLL_STRATEGY_FACTORY,
};

// We use a component instead of a directive here so the user can use implicit
// template reference variables (e.g. #d vs #d="lcsDateTimePicker"). We can change
// this to a directive if angular adds support for `exportAs: '$implicit'` on directives.
/** Component responsible for managing the date time picker overlay/dialog. */
@Component({
   // eslint-disable-next-line @angular-eslint/component-selector
   selector: 'lcs-date-time-picker',
   template: '',
   exportAs: 'lcsDatepicker',
   changeDetection: ChangeDetectionStrategy.OnPush,
   standalone: false,
})
export class LcsDateTimePickerComponent<D> implements OnDestroy {
   /** The date to open the calendar to initially. */
   @Input()
   get startAt(): D | null {
      // If an explicit startAt is set we start there, otherwise we start at whatever the currently
      // selected value is.
      return this._startAt || (this.dateTimePickerInput ? this.dateTimePickerInput.value : null);
   }
   set startAt(value: D | null) {
      this._startAt = this.getValidDateOrNull(this.dateTimeAdapter.deserialize(value));
   }
   private _startAt: D | null;

   /** The view that the calendar should start in. */
   @Input() startView: 'month' | 'year' | 'multi-year' | 'time' = 'month';

   /** Color palette to use on the datepicker's calendar. */
   @Input()
   set color(value: ThemePalette) {
      this._color = value;
   }
   get color(): ThemePalette {
      return this._color || this.dateTimePickerInput?.getThemePalette() || this.defaultColor;
   }
   _color: ThemePalette;

   /** Default color to fall back to if no value is set. */
   defaultColor: ThemePalette | undefined;

   /** Whether the datepicker pop-up should be disabled. */
   @Input()
   set disabled(value: boolean) {
      const newValue = coerceBooleanProperty(value);
      if (newValue !== this._disabled) {
         this._disabled = newValue;
         this.disabledChange.next(newValue);
      }
   }
   get disabled(): boolean {
      return this._disabled === undefined && this.dateTimePickerInput
         ? this.dateTimePickerInput.disabled
         : !!this._disabled;
   }
   private _disabled?: boolean;

   /**
    * Emits selected year in multiyear view.
    * This doesn't imply a change on the selected date.
    */
   @Output() readonly yearSelected: EventEmitter<D> = new EventEmitter<D>();

   /**
    * Emits selected month in year view.
    * This doesn't imply a change on the selected date.
    */
   @Output() readonly monthSelected: EventEmitter<D> = new EventEmitter<D>();

   /** Classes to be passed to the date picker panel. Supports the same syntax as `ngClass`. */
   @Input() panelClass: string | string[];

   /** Function that can be used to add custom CSS classes to dates. */
   @Input() dateClass: (date: D) => MatCalendarCellCssClasses;

   /** Emits when the datepicker has been opened. */
   // eslint-disable-next-line @angular-eslint/no-output-rename
   @Output('opened') openedStream: EventEmitter<void> = new EventEmitter<void>();

   /** Emits when the datepicker has been closed. */
   // eslint-disable-next-line @angular-eslint/no-output-rename
   @Output('closed') closedStream: EventEmitter<void> = new EventEmitter<void>();

   /** Whether the calendar is open. */
   @Input()
   set opened(value: boolean) {
      value ? this.open() : this.close();
   }
   get opened(): boolean {
      return this._opened;
   }
   private _opened = false;

   /** An input indicating the type of the custom header component for the calendar, if set. */
   @Input() calendarHeaderComponent: ComponentType<any>;

   /** The id for the datepicker calendar. */
   id: string = `lcs-date-time-picker-${dateTimePickerUid++}`;

   /** The currently selected date. */
   selected: D | null = null;

   /** The minimum selectable date. */
   get minDate(): D | undefined | null {
      return this.dateTimePickerInput?.min;
   }

   /** The maximum selectable date. */
   get maxDate(): D | undefined | null {
      return this.dateTimePickerInput?.max;
   }

   get dateFilter(): (date: D | null) => boolean {
      return this.dateTimePickerInput?.dateFilter;
   }

   /** The input element this datepicker is associated with. */
   dateTimePickerInput: LcsDateTimePickerInputDirective<D>;

   /** Emits when the datepicker is disabled. */
   readonly disabledChange = new Subject<boolean>();

   /** Emits new selected date when selected date changes. */
   readonly selectedChanged = new Subject<D>();

   private scrollStrategy: () => ScrollStrategy;

   /** A reference to the overlay when the calendar is opened as a overlay. */
   private overlayRef: OverlayRef | null;

   /** Reference to the component instantiated in overlay mode. */
   private overlayComponentRef: ComponentRef<LcsDateTimePickerContentComponent<D>> | null;

   /** The element that was focused before the datepicker was opened. */
   private focusedElementBeforeOpen: HTMLElement | null = null;

   /** Subscription to value changes in the associated input element. */
   private inputSubscription = Subscription.EMPTY;

   constructor(
      private overlay: Overlay,
      private ngZone: NgZone,
      private viewContainerRef: ViewContainerRef,
      @Inject(LCS_DATE_TIME_PICKER_SCROLL_STRATEGY) scrollStrategy: any,
      private dateTimeAdapter: LcsDateTimeAdapter<D>
   ) {
      if (!this.dateTimeAdapter) {
         throw createDateTimePickerError('LcsDateTimeAdapter');
      }

      this.scrollStrategy = scrollStrategy;
   }

   ngOnDestroy(): void {
      this.destroyOverlay();
      this.close();
      this.inputSubscription.unsubscribe();
      this.disabledChange.complete();
   }

   /** Selects the given date */
   select(date: D): void {
      const oldValue = this.selected;
      this.selected = date;
      if (!this.dateTimeAdapter.sameDate(oldValue, this.selected)) {
         this.selectedChanged.next(date);
      }
   }

   /** Emits the selected year in multiyear view */
   emitYearSelected(normalizedYear: D): void {
      this.yearSelected.emit(normalizedYear);
   }

   /** Emits selected month in year view */
   emitMonthSelected(normalizedMonth: D): void {
      this.monthSelected.emit(normalizedMonth);
   }

   /**
    * Register an input with this datepicker.
    * @param input The datepicker input to register with this datepicker.
    */
   registerInput(input: LcsDateTimePickerInputDirective<D>): void {
      if (this.dateTimePickerInput) {
         throw Error('An LcsDateTimePicker can only be associated with a single input.');
      }
      this.dateTimePickerInput = input;
      this.inputSubscription = this.dateTimePickerInput.valueChange.subscribe(
         (value: D | null) => (this.selected = value)
      );
   }

   /** Open the calendar. */
   open(): void {
      if (this._opened || this.disabled) {
         return;
      }
      if (!this.dateTimePickerInput) {
         throw Error('Attempted to open an MatDatepicker with no associated input.');
      }
      if (document) {
         this.focusedElementBeforeOpen = document.activeElement as HTMLElement;
      }

      this.openOverlay();
      this._opened = true;
      this.openedStream.emit();
   }

   /** Close the calendar. */
   close(): void {
      if (!this._opened) {
         return;
      }

      if (this.overlayComponentRef && this.overlayRef) {
         const instance = this.overlayComponentRef.instance;
         instance
            .startExitAnimation()
            .pipe(take(1))
            .subscribe(() => this.destroyOverlay());
      }
      if (this.focusedElementBeforeOpen && typeof this.focusedElementBeforeOpen.focus === 'function') {
         this.focusedElementBeforeOpen.focus();
         setTimeout(this.completeClose.bind(this));
      } else {
         this.completeClose();
      }
   }

   private completeClose(): void {
      this._opened = false;
      this.closedStream.emit();
      this.focusedElementBeforeOpen = null;
   }

   /** Open the calendar as a overlay. */
   private openOverlay(): void {
      const portal = new ComponentPortal<LcsDateTimePickerContentComponent<D>>(
         LcsDateTimePickerContentComponent,
         this.viewContainerRef
      );

      this.destroyOverlay();
      this.createOverlay();
      const ref = (this.overlayComponentRef = this.overlayRef!.attach(portal));
      ref.instance.dateTimePicker = this;
      ref.instance.color = this.color;

      // Update the position once the calendar has rendered.
      this.ngZone.onStable
         .asObservable()
         .pipe(take(1))
         .subscribe(() => {
            this.overlayRef!.updatePosition();
         });
   }

   /** Create the overlay. */
   private createOverlay(): void {
      const overlayConfig = new OverlayConfig({
         positionStrategy: this.createOverlayPositionStrategy(),
         hasBackdrop: true,
         backdropClass: 'mat-overlay-transparent-backdrop', // TODO: lcs
         scrollStrategy: this.scrollStrategy(),
         panelClass: 'mat-datepicker-overlay', // TODO: lcs
      });

      this.overlayRef = this.overlay.create(overlayConfig);
      this.overlayRef.overlayElement.setAttribute('role', 'dialog');

      merge(
         this.overlayRef.backdropClick(),
         this.overlayRef.detachments(),
         this.overlayRef.keydownEvents().pipe(
            filter((event) => {
               // Closing on alt + up is only valid when there's an input associated with the datepicker.
               return (
                  event.keyCode === ESCAPE || (this.dateTimePickerInput && event.altKey && event.keyCode === UP_ARROW)
               );
            })
         )
      ).subscribe((event) => {
         if (event) {
            event.preventDefault();
         }

         this.close();
      });
   }

   /** Destroys the current  overlay. */
   private destroyOverlay() {
      if (this.overlayRef) {
         this.overlayRef.dispose();
         this.overlayRef = this.overlayComponentRef = null;
      }
   }

   /** Create the popup PositionStrategy. */
   private createOverlayPositionStrategy(): PositionStrategy {
      return this.overlay
         .position()
         .flexibleConnectedTo(this.dateTimePickerInput.getConnectedOverlayOrigin())
         .withTransformOriginOn('.lcs-date-time-picker-content')
         .withFlexibleDimensions(false)
         .withViewportMargin(8)
         .withLockedPosition()
         .withPositions([
            {
               originX: 'start',
               originY: 'bottom',
               overlayX: 'start',
               overlayY: 'top',
            },
            {
               originX: 'start',
               originY: 'top',
               overlayX: 'start',
               overlayY: 'bottom',
            },
            {
               originX: 'end',
               originY: 'bottom',
               overlayX: 'end',
               overlayY: 'top',
            },
            {
               originX: 'end',
               originY: 'top',
               overlayX: 'end',
               overlayY: 'bottom',
            },
         ]);
   }

   /**
    * @param obj The object to check.
    * @returns The given object if it is both a date instance and valid, otherwise null.
    */
   private getValidDateOrNull(obj: any): D | null {
      return this.dateTimeAdapter.isDateInstance(obj) && this.dateTimeAdapter.isValid(obj) ? obj : null;
   }

   /* eslint-disable @typescript-eslint/member-ordering */
   /* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match */
   static ngAcceptInputType_disabled: BooleanInput;
   static ngAcceptInputType_touchUi: BooleanInput;
}
